import {
    Assessment,
    GetAssessmentQuery,
    GetAssessmentQueryVariables,
    ListAssessmentsByPropertyQuery,
    ListAssessmentsByPropertyQueryVariables,
    ListAssessmentsByResourceQuery,
    ListAssessmentsByResourceQueryVariables,
    UpdateAssessmentMutation,
    UpdateAssessmentMutationVariables
} from "../../API";
import GraphQLAPI, {
    GraphQLResult
} from "@aws-amplify/api-graphql";
import {
    getAssessment,
    listAssessmentsByProperty,
    listAssessmentsByResource
} from "../../graphql/queries";

import { AssessmentDAO } from "./AssessmentDAO";
import { AuthIDTokenSupplierFactory } from "../auth/AuthIDTokenSupplierFactory";
import ClientLogger from "../logging/ClientLogger";
import ClientLoggerFactory from "../logging/ClientLoggerFactory";
import { IDTokenSupplier } from "../auth/IDTokenSupplier";
import { PermissionResourceType } from "../../models";
import { updateAssessment } from "../../graphql/mutations";
import { ListResponse } from "../util/data/list/ListResponse";

export class GraphQLAssessmentDAO implements AssessmentDAO {
    private static readonly LIST_BY_RESOURCE_ATTEMPT_METRIC_NAME = "GraphQLAssessmentDAO.ListByResource.Attempt";
    private static readonly LIST_BY_RESOURCE_FAILURE_METRIC_NAME = "GraphQLAssessmentDAO.ListByResource.Failure";
    private static readonly LIST_BY_PROPERTY_ATTEMPT_METRIC_NAME = "GraphQLAssessmentDAO.ListByProperty.Attempt";
    private static readonly LIST_BY_PROPERTY_FAILURE_METRIC_NAME = "GraphQLAssessmentDAO.ListByProperty.Failure";
    private static readonly GET_ASSESSMENT_ATTEMPT_METRIC_NAME = "GraphQLAssessmentDAO.getAssessmentById.Attempt";
    private static readonly GET_ASSESSMENT_FAILURE_METRIC_NAME = "GraphQLAssessmentDAO.getAssessmentById.Failure";
    private static readonly UPDATE_ATTEMPT_METRIC_NAME = "GraphQLAssessmentDAO.update.Attempt";
    private static readonly UPDATE_FAILURE_METRIC_NAME = "GraphQLAssessmentDAO.update.Failure";

    private readonly logger: ClientLogger;
    private readonly idTokenSupplier: IDTokenSupplier;

    constructor(
        logger: ClientLogger,
        idTokenSupplier: IDTokenSupplier
    ) {
        this.logger = logger;
        this.idTokenSupplier = idTokenSupplier;
    }

    public async listAssessmentsByResource(
        resourceId: string,
        resourceType: PermissionResourceType
    ): Promise<Array<Assessment>> {
        this.logger.info(
            `Listing Assessment record for resource with id=${resourceId} and type=${resourceType}`,
            { resourceId, resourceType },
            [GraphQLAssessmentDAO.LIST_BY_RESOURCE_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const variables: ListAssessmentsByResourceQueryVariables = {
                resourceId,
                resourceType: {
                    eq: resourceType
                }
            };
            const result: GraphQLResult<ListAssessmentsByResourceQuery> = await GraphQLAPI.graphql({
                query: listAssessmentsByResource,
                variables,
                authToken
            }) as GraphQLResult<ListAssessmentsByResourceQuery>;
            return result.data?.listAssessmentsByResource?.items as Array<Assessment>;
        } catch (error) {
            const errorMessage = `Unable to list Assessment record for resource with id=${resourceId} and type=${resourceType}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLAssessmentDAO.LIST_BY_RESOURCE_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage, { cause: error });
        }
    }

    public async listAssessmentsByProperty(
        propertyId: string,
        nextToken?: string
    ): Promise<ListResponse<Assessment>> {
        this.logger.info(
            `Listing Assessment records for property with id=${propertyId}`,
            { propertyId },
            [GraphQLAssessmentDAO.LIST_BY_PROPERTY_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const variables: ListAssessmentsByPropertyQueryVariables = {
                propertyId,
                nextToken
            };
            const result: GraphQLResult<ListAssessmentsByPropertyQuery> = await GraphQLAPI.graphql({
                query: listAssessmentsByProperty,
                variables,
                authToken
            }) as GraphQLResult<ListAssessmentsByPropertyQuery>;
            return {
                items: result.data?.listAssessmentsByProperty?.items as Array<Assessment>,
                nextToken: result.data?.listAssessmentsByProperty?.nextToken ?? null
            };
        } catch (error) {
            const errorMessage = `Unable to list Assessment records for property with id=${propertyId}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLAssessmentDAO.LIST_BY_PROPERTY_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage, { cause: error });
        }
    }

    public async getAssessmentById(
        id: string
    ): Promise<Assessment> {
        this.logger.info(
            `Getting Assessment record for id=${id}`,
            { id },
            [GraphQLAssessmentDAO.GET_ASSESSMENT_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const variables: GetAssessmentQueryVariables = {
                id: id
            };
            const result: GraphQLResult<GetAssessmentQuery> = await GraphQLAPI.graphql({
                query: getAssessment,
                variables,
                authToken
            }) as GraphQLResult<GetAssessmentQuery>;
            return result.data?.getAssessment as Assessment;
        } catch (error) {
            const errorMessage = `Unable to get Assessment record for id=${id}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLAssessmentDAO.GET_ASSESSMENT_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage, { cause: error });
        }
    }

    public async update(assessment: Assessment): Promise<Assessment> {
        this.logger.info(
            `Updating Assessment record with id=${assessment.id}`,
            { assessment },
            [GraphQLAssessmentDAO.UPDATE_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<UpdateAssessmentMutation> = await GraphQLAPI.graphql({
                query: updateAssessment,
                variables: {
                    input: {
                        id: assessment.id,
                        _version: assessment._version,
                        tagIds: assessment.tagIds,
                        submittedBy: {
                            firstName: assessment.submittedBy?.firstName,
                            lastName: assessment.submittedBy?.lastName
                        },
                        description: assessment.description
                    }
                } satisfies UpdateAssessmentMutationVariables,
                authToken
            }) as GraphQLResult<UpdateAssessmentMutation>;
            return result.data?.updateAssessment as Assessment;
        } catch (error) {
            const errorMessage = `Unable to update Assessment record with id=${assessment.id}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLAssessmentDAO.UPDATE_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage, { cause: error });
        }
    }
}

export const graphQLAssessmentDAO: AssessmentDAO = new GraphQLAssessmentDAO(
    ClientLoggerFactory.getClientLogger("GraphQLAssessmentDAO"),
    AuthIDTokenSupplierFactory.getInstance()
);